import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

/*
  IconButton — квадратная кнопка только с иконкой. `aria-label` обязателен (тип требует),
  иначе скрин-ридер не понимает назначение.

  Размеры совпадают с Button по высоте: sm 32 · md 36 · lg 44; иконка 16 · 18 · 20.
  primary — brand-strong (coral-700, обе темы): белая иконка 5.3:1; coral-400/600 в тёмной теме дают 2.4/3.8.
*/

const iconButtonVariants = cva(
  [
    'inline-flex items-center justify-center rounded-md select-none',
    'transition-interactive',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'active:scale-98',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-strong text-on-brand shadow-sm hover:scale-105 hover:bg-brand-deep',
        secondary:
          'border border-line bg-card text-fg hover:scale-105 hover:border-transparent hover:bg-hover hover:shadow-glow-brand',
        ghost:
          'bg-transparent text-fg-muted hover:scale-105 hover:bg-hover hover:text-fg hover:shadow-glow-brand',
        destructive: 'bg-danger text-on-danger shadow-sm hover:scale-105 hover:brightness-110',
      },
      size: {
        sm: 'size-8 [&_svg]:size-4',
        md: 'size-9 [&_svg]:size-4.5',
        lg: 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
);

export type IconButtonVariant = NonNullable<VariantProps<typeof iconButtonVariants>['variant']>;
export type IconButtonSize = NonNullable<VariantProps<typeof iconButtonVariants>['size']>;

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  /** Обязателен для доступности. */
  'aria-label': string;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant, size, className, children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        data-slot="icon-button"
        className={cn(iconButtonVariants({ variant, size }), className)}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';
